import { Formik } from 'formik'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo } from 'react'
import * as Yup from 'yup'

import { FormListener } from '@/@bothrs-ui/web/components/forms/FormListener'
import { mixpanel } from '@/mixpanel'
import { slideAnimation } from '@/modules/shared/animations/slide.animation'
import { useLoggedInForMandatee } from '@/modules/trust/hooks/useLoggedInForMandatee'
import { isIntramuros } from '@/utils/intramuros'
import { ARRAY_TYPES, STRUCTURED_OBSERVATION_TYPES } from '../../enums/questionnaire.enums'
import { useQuestionnaireEngine } from '../../hooks/useQuestionnaireEngine'
import { useQuestionnaireValidationSchema } from '../../hooks/useQuestionnaireValidationSchema'
import { QuestionnaireQuestion } from '../questionnaire-question/QuestionnaireQuestion'
import { StForm, StQuestionContainer, StQuestionnaireContainer } from './Questionnaire.styled'

import type { IQuestionnaireQuestion, MultiSelectCommentAnswerItem, QuestionAnswerInput } from '@healthblocks-io/core'
import type { Path } from 'ramda'

type QuestionnaireProps = {
  animationDirection: 'forward' | 'backward'
  currentQuestion: IQuestionnaireQuestion | null
  onSubmitAnswer: (answer: QuestionAnswerInput, autoAdvance?: boolean) => void
  onNext: () => void
  onPrevious?: () => void
  isSaving?: boolean
}

const AnimatedStQuestionContainer = motion(StQuestionContainer)

/**
 * Questionnaire
 *
 * Renders a single question at a time using Formik.
 * This component is responsible for validation and submitting the
 * current answer back to the questionnaire engine.
 *
 * Responsibilities:
 *  - Builds a per-question Yup validation schema from the full questionnaire schema
 *  - Initializes Formik with only the current question's value (prefilled from engine state)
 *  - Validates the current question on change and submit
 *  - On submit:
 *      → Calls onSubmitAnswer callback (from parent) which:
 *        → Calls setAnswer({ linkId, value, text }) to update engine state
 *        → Calls saveProgress() to persist the answer
 *        → Calls goNext() if not the last question, or submitQuestionnaire() if complete
 *  - Animates transitions between questions (forward/backward)
 *  - Provides `nextDisabled` state to child component based on Formik validity
 *
 * Props:
 * @param {function} onNext      — Callback executed after successful submit
 * @param {function} onPrevious  — Callback for navigating backward
 * @param {'forward'|'backward'} animationDirection — Animation direction for transitions
 *
 * Internally:
 *  - Uses useQuestionnaireEngine() to read the current question
 *  - Uses enableReinitialize Formik mode so prefilled values always load correctly
 *
 * Example:
 * <Questionnaire animationDirection="forward" />
 */
const Questionnaire = ({
  currentQuestion,
  animationDirection,
  onSubmitAnswer,
  onNext,
  onPrevious,
  isSaving,
}: QuestionnaireProps) => {
  const { state } = useQuestionnaireEngine()
  const isLoggedInForMandatee = useLoggedInForMandatee()

  // Hooks
  // ------------------------------------------------------------------------- /
  const formValidationSchema = useQuestionnaireValidationSchema(state.questionnaire)

  // Template variables
  // ------------------------------------------------------------------------- /
  const linkId = currentQuestion?.linkId

  const initialValues = useMemo(() => {
    if (!linkId) return {}
    const answer = state.answers[linkId]
    const questionType = currentQuestion?.type

    if (questionType && STRUCTURED_OBSERVATION_TYPES.has(questionType)) {
      return { [linkId]: answer?.value }
    }

    // For array-based question types (Array, MultiSelect), default to []
    if (questionType && ARRAY_TYPES.has(questionType)) {
      const value = answer?.value
      return { [linkId]: Array.isArray(value) ? value : [] }
    }

    // For other types, default to ''
    return { [linkId]: answer?.value ?? '' }
  }, [linkId, state.answers, currentQuestion?.type])

  const questionValidationSchema = useMemo(() => {
    if (!linkId) return Yup.object()

    // Yup object schemas hebben intern .fields
    const fieldSchema = (formValidationSchema as any).fields?.[linkId]

    if (!fieldSchema) {
      // Geen schema voor deze vraag? Dan geen validatie.
      return Yup.object()
    }

    // Maak een schema enkel voor dit veld
    return Yup.object({
      [linkId]: fieldSchema,
    })
  }, [formValidationSchema, linkId])

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (!state.questionnaire.name) return

    mixpanel.track('questionnaire_start', {
      questionnaire_slug: state.questionnaire.name,
      is_mandate: isLoggedInForMandatee,
      is_intramuros: isIntramuros,
    })
  }, [state.questionnaire.name])

  // Interaction Methods
  // ------------------------------------------------------------------------- /

  function handleFormChange(values: Record<string, unknown>, isValid: boolean) {
    if (!linkId || !isValid) return

    const rawValue = values[linkId]

    const currentEngineAnswer = state.answers[linkId]?.value

    // Avoid loop/initial run if values are effectively same
    if (JSON.stringify(rawValue) === JSON.stringify(currentEngineAnswer)) {
      return
    }

    handleSubmit(values)
  }

  /**
   * Normalizes a MultiSelectComment answer at the form → persistence boundary.
   *
   * Contract: a persisted item either carries meaningful comment text or no
   * `comment` key at all — consumers (answer text, display resolution,
   * isCommentRequired validation, the Synops Waarde round-trip) never see
   * `comment: ''` or whitespace-only comments and therefore never need their
   * own "is it really empty" check.
   *
   * This normalization deliberately lives here and NOT in the component:
   * the comment input is controlled by Formik state (`item.comment ?? ''`)
   * and every change auto-submits. If the component stripped whitespace
   * while writing to Formik, typing a leading space (or clearing back to
   * spaces mid-edit) would immediately reset the input under the user's
   * cursor. So form state keeps what was typed verbatim; only the persisted
   * answer is cleaned:
   *
   *   form state:  [{ value: 'andere', comment: ' ' }]
   *   persisted:   [{ value: 'andere' }]
   */
  function normalizeAnswerValue(rawValue: unknown): unknown {
    if (currentQuestion?.type !== 'MultiSelectComment' || !Array.isArray(rawValue)) {
      return rawValue
    }

    return (rawValue as MultiSelectCommentAnswerItem[]).map((item) =>
      item.comment?.trim() ? item : { value: item.value },
    )
  }

  function handleSubmit(values: Record<string, unknown>) {
    if (!linkId) return
    const value = normalizeAnswerValue(values[linkId as string])

    onSubmitAnswer({ linkId, value })
  }

  /**
   * Manually submit an answer to the question without formik
   * @param answer - The answer to submit
   * @param autoAdvance - Whether to automatically advance to the next question
   */
  function handleManuallySubmitAnswer(answer: QuestionAnswerInput & { fileIds: number[] }, autoAdvance: boolean) {
    if (!linkId) return
    const value = normalizeAnswerValue(answer.value)

    onSubmitAnswer({ linkId, value }, autoAdvance)
  }

  function handleNext() {
    // Track health parameter confirmation/update
    if (currentQuestion?.type === 'Observation' && linkId) {
      const answerValue = state.answers[linkId]?.value

      if (answerValue === 'keep' || answerValue === 'edit') {
        const eventName = answerValue === 'keep' ? 'health_parameter_confirmed' : 'health_parameter_updated'

        mixpanel.track(eventName, {
          parameter_type: linkId.replace(/_review$/, ''),
          questionnaire_slug: state.questionnaire.name,
          is_mandate: isLoggedInForMandatee,
          is_intramuros: isIntramuros,
        })
      }
    }

    onNext()
  }

  // Render Methods
  // ------------------------------------------------------------------------- /
  // Allow Display questions without linkId, but still need currentQuestion
  if (!currentQuestion || (currentQuestion.type !== 'Display' && !linkId)) return null

  return (
    <Formik
      initialValues={initialValues}
      // Very important to reinitialize the form when the question changes
      enableReinitialize={true}
      onSubmit={handleSubmit}
      validationSchema={questionValidationSchema}
      validateOnChange
      validateOnMount
    >
      {({ errors }) => {
        const fieldError = linkId ? errors[linkId] : undefined
        const hasError = !!fieldError

        return (
          <StQuestionnaireContainer>
            <StForm noValidate>
              <FormListener fields={[linkId ?? ''] as unknown as Path[]} errors={errors} onChange={handleFormChange} />
              <AnimatePresence mode='popLayout' initial={false}>
                <AnimatedStQuestionContainer
                  key={currentQuestion.id}
                  variants={slideAnimation}
                  initial={animationDirection === 'forward' ? 'slideLeft' : 'slideRight'}
                  animate='visible'
                  exit={animationDirection === 'forward' ? 'slideRight' : 'slideLeft'}
                  transition={{ type: 'spring', bounce: 0.1, duration: 0.4 }}
                >
                  <QuestionnaireQuestion
                    data={currentQuestion}
                    onManuallySubmitAnswer={handleManuallySubmitAnswer}
                    onNext={handleNext}
                    onPrevious={onPrevious}
                    nextDisabled={hasError}
                    isSaving={isSaving}
                  />
                </AnimatedStQuestionContainer>
              </AnimatePresence>
            </StForm>
          </StQuestionnaireContainer>
        )
      }}
    </Formik>
  )
}

export { Questionnaire }
